import React, { useState } from "react";
import {
  List,
  ListItem,
  Switch,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, Divider } from "@mui/material";
import {
  DropDownCategoryEnumModel,
  ILookupItemModel,
  IPagedResultOfLookupItemModel,
} from "../../types/types";
import { useQuery } from "@tanstack/react-query";
import LoadingDialog from "../Dialogs/LoadingDialog";

interface PagedListProps {
  category: DropDownCategoryEnumModel;
  items: ILookupItemModel[];
  editingOptionId?: number;
  editedLabel: string;
  onEditLabelChange: (value: string) => void;
  onSaveEdit: (optionId: number) => void;
  onEditOption: (option: ILookupItemModel) => void;
  onDeleteOption: (option: ILookupItemModel) => void;
  searchFn?: (
    pageIndex: number,
    pageSize: number,
    keyword: string,
  ) => Promise<IPagedResultOfLookupItemModel>;
  allowPaging: boolean;
}

const PagedList: React.FC<PagedListProps> = ({
  category,
  items: initialItems,
  editingOptionId,
  editedLabel,
  onEditLabelChange,
  onSaveEdit,
  onEditOption,
  onDeleteOption,
  searchFn,
  allowPaging,
}) => {
  // const [items, setItems] = useState<ILookupItemModel[]>(initialItems);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: [`searchFn${category.toString()}`, pageIndex, pageSize, keyword],
    queryFn: async () => {
      const result = await searchFn!(pageIndex, pageSize, keyword);
      return result;
    },
    refetchOnWindowFocus: false,
    enabled: searchFn !== undefined,
  });
  // useEffect(() => {
  //   if (searchFn && allowPaging) {
  //     const fetchItems = async () => {
  //       setLoading(true);
  //       setError(null);
  //       try {
  //         const result = await searchFn(pageIndex, pageSize, keyword);
  //         setItems(result.result || []);
  //       } catch (err) {
  //         setError("Failed to load items");
  //         console.error(err);
  //       } finally {
  //         setLoading(false);
  //       }
  //     };
  //     fetchItems();
  //   }
  // }, [pageIndex, keyword, searchFn, allowPaging]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setPageIndex(1); // Reset to first page on new search
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {allowPaging && searchFn && (
        <Box
          sx={{
            background: "#F7F9FB",
            borderRadius: "8px",
            px: 2,
            py: 2,
            mb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: "#222",
              mb: 0.5,
              fontSize: "1rem",
              letterSpacing: 0.2,
            }}
          >
            Search Options
          </Typography>
          <TextField
            label="Search"
            value={keyword}
            onChange={handleSearch}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#1976d2" }} />
                </InputAdornment>
              ),
              sx: {
                background: "#fff",
                borderRadius: "6px",
                fontSize: "1rem",
                py: 1,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "1rem",
                background: "#fff",
              },
            }}
          />
          <Divider sx={{ mt: 2, mb: 0 }} />
        </Box>
      )}
      <LoadingDialog open={isLoading} />
      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      <List sx={{ minHeight: "200px" }}>
        {data?.result?.length === 0 && (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "#888", mt: 2 }}
          >
            No options found.
          </Typography>
        )}
        {data?.result?.map((option) => (
          <ListItem
            key={option.id}
            disableGutters
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#F7F9FB",
              borderRadius: "8px",
              px: 2,
              py: 1.5,
              mb: 1.5,
            }}
          >
            <Switch
              checked={option.deleted !== true}
              sx={{
                mr: 2,
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "primary.main",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#1976d2",
                },
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                color: option.deleted === true ? "#B0B0B0" : "#222",
                fontSize: "1.1rem",
                mr: 1,
                flexGrow: 1,
              }}
            >
              {option.name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              {editingOptionId === option.id ? (
                <Button
                  onClick={() => onSaveEdit(option.id!)}
                  variant="outlined"
                  size="small"
                  sx={{ minWidth: 0, px: 1, mr: 1 }}
                >
                  Save
                </Button>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => onEditOption(option)}
                  sx={{
                    minWidth: 0,
                    p: 1,
                    color: "#222",
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                onClick={() => onDeleteOption(option)}
                sx={{
                  minWidth: 0,
                  p: 1,
                  color: "#D32F2F",
                }}
                size="small"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </ListItem>
        ))}
      </List>

      {allowPaging && searchFn && (
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            disabled={pageIndex <= 1 || isLoading}
            onClick={() => setPageIndex((p) => p - 1)}
            variant="outlined"
          >
            Previous
          </Button>
          <Typography>Page {pageIndex}</Typography>
          <Button
            disabled={
              isLoading || (data?.totalCount ?? 0) <= pageSize * pageIndex
            }
            // hidden={(data?.totalCount ?? 0) <= pageSize * pageIndex}
            onClick={() => setPageIndex((p) => p + 1)}
            variant="outlined"
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PagedList;
